import React, { createContext, useContext, useMemo } from 'react';
import {
  isFeatureEnabled,
  hasAnyFeature,
  hasAllFeatures,
  featureStatesAreEqual,
  type FeatureState,
} from '../core/featureUtils';

interface FeatureContextValue {
  features: FeatureState;
  isLoading: boolean;
  error: Error | null;
}

const FeatureContext = createContext<FeatureContextValue | null>(null);

interface FeatureProviderProps {
  features: FeatureState;
  isLoading?: boolean;
  error?: Error | null;
  children: React.ReactNode;
}

export const FeatureProvider: React.FC<FeatureProviderProps> = ({
  features,
  isLoading = false,
  error = null,
  children,
}) => {
  // Stable features reference to reduce rerenders (pattern from PermissionContext)
  const stableFeaturesRef = React.useRef<FeatureState>(features);

  if (!featureStatesAreEqual(stableFeaturesRef.current, features)) {
    stableFeaturesRef.current = features;
  }

  const value = useMemo<FeatureContextValue>(
    () => ({
      features: stableFeaturesRef.current,
      isLoading,
      error,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stableFeaturesRef.current, isLoading, error]
  );

  return (
    <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>
  );
};

const useFeatureContext = (): FeatureContextValue => {
  const ctx = useContext(FeatureContext);
  if (!ctx) {
    throw new Error(
      'Feature context not found. Wrap your app with FeatureProvider.'
    );
  }
  return ctx;
};

export const useFeatures = (): FeatureState => useFeatureContext().features;

export const useFeaturesLoading = (): boolean => useFeatureContext().isLoading;

export const useFeaturesError = (): Error | null => useFeatureContext().error;

export const useFeature = (key: string): boolean => {
  const { features } = useFeatureContext();
  return useMemo(() => isFeatureEnabled(features, key), [features, key]);
};

export const useAnyFeature = (keys: string[]): boolean => {
  const { features } = useFeatureContext();
  return useMemo(() => hasAnyFeature(features, keys), [features, keys]);
};

export const useAllFeatures = (keys: string[]): boolean => {
  const { features } = useFeatureContext();
  return useMemo(() => hasAllFeatures(features, keys), [features, keys]);
};
